/**
 * Per-request security headers for the credential vault pages (`/vault/*`),
 * set by `proxy.ts`. Vault Phase 5 review, finding H2 (interim fix; the full
 * fix is a dedicated origin — decision D-18, PROPOSED).
 *
 * The vault unlock token lives in page JS on Hub's MAIN origin. Two things
 * stand between it and the rest of Hub:
 *
 *  1. A STRICT, NONCE-BASED CSP on every vault document: `script-src 'self'
 *     'nonce-<per-request>' 'strict-dynamic'` — no `'unsafe-inline'`, no
 *     `'unsafe-eval'` (except `next dev`), no third-party script host. An
 *     injected inline script or an attacker-chosen script URL does not run.
 *     Next.js stamps the nonce on its own scripts because the nonce is in the
 *     REQUEST's CSP header and the vault layout renders per request
 *     (`app/vault/layout.tsx`: `instant = false` + a request-time viewport, so
 *     there is no prerendered shell whose scripts would lack the nonce).
 *  2. `Cross-Origin-Opener-Policy: same-origin`: a vault document is put in its
 *     own browsing-context group, so a Hub page that `window.open`s it (the
 *     exploit path) gets a dead handle, and the vault page sees `opener=null`.
 *
 * Pure (no `server-only`) so the proxy and the tests import the same builder.
 */

/** `/vault` and everything under it — the vault PAGES (not `/api/vault/*`). */
export function isVaultPagePath(pathname: string): boolean {
  return pathname === "/vault" || pathname.startsWith("/vault/");
}

/** The reveal page shows plaintext: no remote images at all there. */
export function isVaultRevealPath(pathname: string): boolean {
  return pathname.startsWith("/vault/reveal/");
}

/** 128 bits of CSPRNG, base64 — fresh for every request. */
export function createCspNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** `https://host[:port]` of a URL, or null when it is missing / not http(s). */
export function httpOrigin(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:" ? url.origin : null;
  } catch {
    return null;
  }
}

export interface VaultCspInputs {
  readonly nonce: string;
  readonly reveal: boolean;
  /** `next dev` only: React needs `eval` for its debugging stacks. */
  readonly dev: boolean;
  /** `NEXT_PUBLIC_SUPABASE_URL` — pinned to this env's ONE project host. */
  readonly supabaseUrl: string | undefined;
  /** `VAULT_HTTP_URL` — the vault's browser-facing API (unlock/redeem/TOTP). */
  readonly vaultHttpUrl: string | undefined;
}

/**
 * Trusted Types (`require-trusted-types-for 'script'`) is deliberately NOT in
 * the enforced policy: it was tried and breaks Next.js/Turbopack chunk loading
 * (see `trustedTypesReportOnly` and threat model T26). It ships report-only so
 * violations are visible without breaking the page.
 */
export function buildVaultPageCsp(inputs: VaultCspInputs): string {
  const supabase = httpOrigin(inputs.supabaseUrl);
  const vault = httpOrigin(inputs.vaultHttpUrl);
  const connect = ["'self'"];
  if (supabase) connect.push(supabase, supabase.replace(/^http/, "ws"));
  if (vault) connect.push(vault);

  const directives: Array<[string, string[]]> = [
    ["default-src", ["'self'"]],
    ["script-src", ["'self'", `'nonce-${inputs.nonce}'`, "'strict-dynamic'", ...(inputs.dev ? ["'unsafe-eval'"] : [])]],
    ["script-src-attr", ["'none'"]],
    ["style-src", ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"]],
    ["img-src", inputs.reveal ? ["'self'", "data:"] : ["'self'", "data:", "https:"]],
    ["font-src", ["'self'", "data:", "https://fonts.gstatic.com"]],
    ["connect-src", connect],
    ["worker-src", ["'self'"]],
    ["manifest-src", ["'self'"]],
    ["frame-src", ["'none'"]],
    ["frame-ancestors", ["'none'"]],
    ["base-uri", ["'none'"]],
    ["form-action", ["'self'"]],
    ["object-src", ["'none'"]],
  ];
  return directives.map(([name, values]) => `${name} ${values.join(" ")}`).join("; ");
}

/** Report-only companion: Trusted Types, observed but not enforced (see above). */
export function trustedTypesReportOnly(): string {
  return "require-trusted-types-for 'script'";
}

/** Response headers every vault document carries (in addition to next.config's no-store set). */
export function vaultPageResponseHeaders(csp: string): Record<string, string> {
  return {
    "Content-Security-Policy": csp,
    "Content-Security-Policy-Report-Only": trustedTypesReportOnly(),
    "Cross-Origin-Opener-Policy": "same-origin",
    "Cross-Origin-Resource-Policy": "same-origin",
    "Cache-Control": "no-store, max-age=0",
    "Referrer-Policy": "no-referrer",
    "X-Frame-Options": "DENY",
  };
}
